<script setup>
import { onMounted, ref, watch } from 'vue'
import { Loader } from '@googlemaps/js-api-loader'
import { GoogleMap, CustomMarker, InfoWindow } from 'vue3-google-map'
import debounce from 'lodash/debounce'
import gtfsApi from '@/Shared/Services/gtfs.api.js'
import MapMarkerIcon from '@/Shared/Components/Markers/MapMarkerIcon.vue'
import BusMapMarkerIcon from '@/Shared/Components/Markers/BusMapMarkerIcon.vue'
import BusSearch from '@/Public/Partials/ExploreBusOption/BusSearch.vue'
import { usePositionStore } from '@/Public/Stores/PositionStore'
import MapMarkerWrapper from '@/Public/MapMarkerCards/MapMarkerWrapper.vue'
import { useUserSettingsStore } from '@/Public/Stores/userSettingsStore'
import { useJourneyStore } from '@/Public/Stores/JourneyStore'
import { hasSignificantBoundsChange } from '@/Shared/Helpers/mapHelper'

let newPositionStore = usePositionStore()
let newUserSettingsStore = useUserSettingsStore()
let newJourneyStore = useJourneyStore()

const loader = new Loader({
  apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
  version: 'weekly',
  libraries: ['geometry', 'places'],
})

const map = ref(null)
const markers = ref(null)
const loading = ref(false)
const apiPromise = loader.load()
const selectedMarkerId = ref(null)
const center = ref({ lat: 51.15611, lng: 0.87411 })

const showMarkers = ref(null)
const mapZoom = ref(12)
const previousBounds = ref(null)
const boundsChangedThreshold = 0.2 //20%percent

const kentBounds = ref({
  north: 51.55617,
  south: 49.82631,
  west: -0.4012,
  east: 1.661,
})

const onMapMove = debounce((reload) => {
  const bounds = map.value.map.getBounds()

  if (!bounds) return
  //added to prevent too many calls to the qpi and to prevent modal closing on the opening due to map bounds changed
  if (
    previousBounds.value &&
    !hasSignificantBoundsChange(previousBounds.value, bounds, boundsChangedThreshold) &&
    !reload
  ) {
    return // Ignore small changes
  }

  mapZoom.value = map.value.map.zoom

  gtfsApi
    .markersNearest(bounds.getNorthEast().toJSON(), bounds.getSouthWest().toJSON(), mapZoom.value, showMarkers.value)
    .then((response) => {
      loading.value = false
      if (response.status === 200) {
        markers.value = response.data
        newUserSettingsStore.availableMarkers = markers.value[markers.value.length - 1]
      }
      if (markers.value && newPositionStore.hasBusStopPosition) {
        const pickedBusStopMarker = markers.value.find((stop) => stop.stop_code === newPositionStore.busStopPosition.id)
        selectedMarkerId.value = newPositionStore.busStopPosition.id
        toggleMarkerInfo(pickedBusStopMarker)
        //resetting search for bus stop
        newPositionStore.busStopPosition = null
      }
      if (markers.value && newPositionStore.hasPoiPosition) {
        const poiMarker = markers.value.find((poi) => poi.id === newPositionStore.poiPosition.id)
        selectedMarkerId.value = newPositionStore.poiPosition.id
        toggleMarkerInfo(poiMarker)
        //resetting search for poi
        newPositionStore.poiPosition = null
      }
    })
  previousBounds.value = bounds
}, 500)

const toggleMarkerInfo = (marker) => {
  selectedMarkerId.value = marker.id
}

const hideInfoWindow = () => {
  selectedMarkerId.value = null
}

onMounted(() => {
  showMarkers.value = newUserSettingsStore.mapSearchFilter
})

//watchers
watch(
  () => newPositionStore.searchablePosition,
  (newVal) => {
    mapZoom.value = 14
    center.value = newVal
  },
)

watch(
  () => newPositionStore.busStopPosition,
  (newVal) => {
    center.value = newVal
    mapZoom.value = 18
  },
)

watch(
  () => newPositionStore.poiPosition,
  (newVal) => {
    center.value = newVal
    mapZoom.value = 18
  },
)

watch(
  () => newUserSettingsStore.mapSearchFilter,
  (newVal) => {
    showMarkers.value = newVal
    //forcing reload
    onMapMove(true)
  },
)
const zoomIn = (marker) => {
  // fine tuning of the map need to zoom in sometimes to prevent showing same data all over again where some clusters might be
  //closely coupled
  // if (marker.cluster_id === null) {
  //   mapZoom.value = mapZoom.value + 2
  //   // } else if (marker.count < 10) {
  //   //   mapZoom.value = 16
  // } else {
  //   mapZoom.value++
  // }

  const [swLng, swLat] = marker.south_west.replace(/[()]/g, '').split(', ').map(Number)
  const [neLng, neLat] = marker.north_east.replace(/[()]/g, '').split(', ').map(Number)

  // Create LatLng objects from the parsed coordinates
  const southWest = new google.maps.LatLng(swLat, swLng)
  const northEast = new google.maps.LatLng(neLat, neLng)

  // Create a LatLngBounds object and extend it with both corners
  const mapBounds = new google.maps.LatLngBounds()
  mapBounds.extend(southWest)
  mapBounds.extend(northEast)

  // Fit the map to the specified bounds
  map.value.map.fitBounds(mapBounds)
}

const getClusterColor = (clusterType) => {
  switch (clusterType) {
    case 'stop_cluster':
      return 'bg-[#900666] text-white'
    case 'poi_cluster':
      return 'bg-green text-white'
    case 'train_cluster':
      return 'bg-yellow text-black'
    case 'ferry_cluster':
      return 'bg-[#248096] text-white'
    case 'ev_cluster':
      return 'bg-[#2362AD] text-white'
    case 'taxi_cluster':
      return 'bg-black text-white'
    default:
      return 'bg-gray-500'
  }
}

const setUserJourney = () => {
  const foundItem = markers.value.find((item) => item.id === selectedMarkerId.value)
  const latLng = {
    lat: parseFloat(foundItem.position_latitude),
    lng: parseFloat(foundItem.position_longitude),
  }
  //setting user destination for journeyPlanner component
  newJourneyStore.setDestination(latLng)
}
</script>

<template>
  <GoogleMap
    ref="map"
    :api-promise="apiPromise"
    :center="center"
    class="w-full h-[624px] my-4"
    :zoom="mapZoom"
    :disable-default-ui="true"
    :zoom-control="true"
    :fullscreen-control="true"
    :fullscreen-control-position="'RIGHT_BOTTOM'"
    :scale-control="true"
    :min-zoom="8"
    :max-zoom="20"
    :restriction="{
      latLngBounds: kentBounds,
      strictBounds: true,
    }"
    :styles="[
      {
        featureType: 'poi.business',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit.station.bus',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit.station.rail',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ]"
    @bounds_changed="onMapMove"
  >
    <CustomMarker
      v-for="marker in markers"
      :key="marker.id"
      :options="{
        position: { lat: parseFloat(marker.position_latitude), lng: parseFloat(marker.position_longitude) },
      }"
    >
      <div v-if="marker.type == 'cluster'">
        <div
          :class="[
            'flex items-center justify-center w-8 h-8 rounded-full  font-bold',
            marker.count > 99 ? 'text-sm' : 'text-xl',
            getClusterColor(marker.cluster_type),
          ]"
          @click="zoomIn(marker)"
        >
          {{ marker.count }}
        </div>
      </div>
      <div
        v-else
        class="relative cursor-pointer"
        role="button"
        @click="toggleMarkerInfo(marker)"
      >
        <!-- Display InfoWindow if the marker's ID matches the selectedMarkerId -->
        <InfoWindow
          v-if="selectedMarkerId === marker.id"
          :options="{
            position: { lat: parseFloat(marker.position_latitude), lng: parseFloat(marker.position_longitude) },
            headerDisabled: true,
            minWidth: 385,
          }"
        >
          <div>
            <MapMarkerWrapper
              :marker="marker"
              @close="hideInfoWindow"
              @journey="setUserJourney"
            />
          </div>
        </InfoWindow>

        <!-- Marker icon or content -->
        <div>
          <MapMarkerIcon
            v-if="marker.type != 'live-bus'"
            :marker-type="marker.type"
          />
          <BusMapMarkerIcon
            v-else
            class="transform"
            :data="marker.data"
          />
        </div>
      </div>
    </CustomMarker>
  </GoogleMap>
  <div class="md:hidden absolute top-0 left-0 w-full px-2">
    <BusSearch />
  </div>
</template>

<!-- :style="`--rotation: ${marker.data.position_bearing}deg;`" -->
